import { useState } from 'react';
import React from 'react';
import { IconButton, useMediaQuery, useTheme } from '@mui/material';
import {
  CloseIcon,
  KioskIcon,
  TabletIcon,
  TVIcon
} from '@shared/components/icons/svg-icon';

import tabletImage from '@assets/images/branding/tablet-image.png';
import tvImage from '@assets/images/branding/tv-image.png';
import kioskImage from '@assets/images/branding/kiosk.png';
import { DigitalSignageScreensModel } from '@modules/digital-signage/model/screens.model.ts';
import ScreenMediaPreview from '@modules/digital-signage/components/screen-media-preview/screen-media-preview.tsx';

interface ScreenPreviewModalProps {
  onClose: () => void;
  data: DigitalSignageScreensModel | null;
}

type ScreenType = 'tv' | 'tablet' | 'kiosk';

const ScreenPreviewModal: React.FC<ScreenPreviewModalProps> = ({
  onClose,
  data
}) => {
  const [screenType, setScreenType] = useState<ScreenType>('tv');
  const theme = useTheme();
  const isXL = useMediaQuery(theme.breakpoints.up('xl'));
  const zoomLevel = isXL ? 1 : 0.75;

  type ScreenConfig = {
    bg: string;
    innerWidth: number;
    innerHeight: number;
    innerTop?: number;
  };

  const config: Record<ScreenType, ScreenConfig> = {
    tv: {
      bg: tvImage,
      innerWidth: 1238,
      innerHeight: 696,
      innerTop: -13
    },
    tablet: {
      bg: tabletImage,
      innerWidth: 1057,
      innerHeight: 613
    },
    kiosk: {
      bg: kioskImage,
      innerWidth: 392,
      innerHeight: 700
    }
  };

  const active = config[screenType];

  return (
    <div
      className="w-full h-full fixed inset-0 z-[9999] flex flex-col top-0 left-0 items-center pt-10"
      style={{
        zoom: zoomLevel,
        background:
          'radial-gradient(circle at top left, #f9fafc 0%, #e3e8f0 40%, var(--primary) 100%)'
      }}
    >
      <div className="absolute top-8 right-8">
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{
            backgroundColor: 'rgba(255,255,255,0.7)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.9)',
              transform: 'scale(1.05)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </div>

      <div className="flex gap-4 justify-center mb-10 backdrop-blur-md p-4 rounded-xl bg-white/60 shadow-sm border border-white/30">
        {[
          { type: 'tv', Icon: TVIcon },
          { type: 'tablet', Icon: TabletIcon },
          { type: 'kiosk', Icon: KioskIcon }
        ].map(({ type, Icon }) => (
          <IconButton
            key={type}
            disableRipple
            onClick={() => setScreenType(type as ScreenType)}
            sx={{
              width: 64,
              height: 64,
              borderRadius: '16px',
              border: '1px solid',
              borderColor:
                screenType === type ? 'var(--primary)' : 'rgb(213 215 229)',
              backgroundColor:
                screenType === type ? 'rgba(63, 81, 181, 0.1)' : 'white',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(63, 81, 181, 0.15)',
                transform: 'translateY(-3px)'
              }
            }}
          >
            <Icon
              stroke={screenType === type ? 'var(--primary)' : 'var(--dark-2)'}
              fill={
                type === 'kiosk' && screenType === type
                  ? 'var(--primary)'
                  : '#2E2E2E'
              }
            />
          </IconButton>
        ))}
      </div>

      <div
        className="relative bg-center bg-no-repeat bg-contain transition-all duration-500"
        style={{
          width: '1300px',
          height: '765px',
          backgroundImage: `url(${active.bg})`
        }}
      >
        <div
          className="absolute overflow-hidden shadow-lg flex items-center justify-center transition-all duration-500"
          style={{
            width: `${active.innerWidth}px`,
            height: `${active.innerHeight}px`,
            marginTop: screenType === 'tv' ? `${active.innerTop}px` : '0',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)'
          }}
        >
          <div
            className="w-full h-full transition-all duration-500"
            style={{ overflow: 'hidden' }}
          >
            <ScreenMediaPreview screen={data} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScreenPreviewModal;
